/**
 * LanguageSelector - Simple language selection for Speech-to-Text
 *
 * A minimal dropdown to select the language for STT transcription.
 */

import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from "@mui/material";

// Types
interface Language {
  code: string;
  label: string;
  flag: string;
}

// Supported languages. English first, Swahili (Kenya) second — initial beta users are Kenyan.
// Swahili uses the BCP-47 "sw-KE" tag so it matches the CareStart voice translate engine (VoxPont2).
const SUPPORTED_LANGUAGES: Language[] = [
  { code: "en-US", label: "English (US)", flag: "🇺🇸" },
  { code: "sw-KE", label: "Swahili (Kenya)", flag: "🇰🇪" },
  { code: "en-GB", label: "English (UK)", flag: "🇬🇧" },
  { code: "fr-FR", label: "French", flag: "🇫🇷" },
  { code: "es-ES", label: "Spanish", flag: "🇪🇸" },
  { code: "pt-BR", label: "Portuguese", flag: "🇧🇷" },
  { code: "ar-SA", label: "Arabic", flag: "🇸🇦" },
  { code: "hi-IN", label: "Hindi", flag: "🇮🇳" },
];

// Props
interface LanguageSelectorProps {
  value: string;
  onChange: (language: string) => void;
  disabled?: boolean;
  label?: string;
}

/**
 * The label differs by caller because the setting does different things.
 *
 * In CareStart the same value picks the voice the patient is answered in, so
 * "Spoken language" is literal — it is the language that gets spoken. ChartMind
 * never speaks: there the value only sets the speech-recognition locale, and
 * "Spoken language" reads like a promise of audio the step does not make. That
 * caller passes "Language" instead.
 */
const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  label = "Spoken language",
}) => {
  const handleChange = (e: SelectChangeEvent) => {
    onChange(e.target.value);
  };

  return (
    <FormControl size="small" sx={{ width: 240 }} disabled={disabled}>
      <InputLabel id="language-selector-label">{label}</InputLabel>
      <Select
        labelId="language-selector-label"
        value={value}
        label={label}
        onChange={handleChange}
      >
        {SUPPORTED_LANGUAGES.map((lang: Language) => (
          <MenuItem key={lang.code} value={lang.code}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span>{lang.flag}</span>
              <span style={{ fontSize: "0.85rem" }}>{lang.label}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;
export { SUPPORTED_LANGUAGES };
export type { Language };
